import type { FC } from "react";

import { Card, CardRowDetail, CardRowDetailColumn, CardRowDetailLine, CardRowDetailLineSpacer } from "widgets";
import { formatNumberByThousands } from "widgets/utils";

import { SmartLink } from "../../core/SmartLink.js";
import { ProjectGroupsRow } from "../../core/cards/index.js";
import { SelectRangeButton } from "../SelectRangeButton.js";
import type { IConditionNameLookup } from "./FeatureTrack.js";
import { capitalizeFirstLetterOfAllWords } from "@/app-utils/string.js";
import type { IExpressionParams, IPileupFeature } from "@/app-utils/track-shapes/track-pileup-factory.js";
import type { Feature, Group, Project } from "@/graphql.js";
import { ENTITY_TYPE, FEATURE_DISPLAY_NAME, SEQUENCE_TYPE_LABEL } from "@/settings/settings.js";

const TrackExpressionTooltip: FC<{
  expression: IExpressionParams;
  expressionColoringConditionNameLookup: IConditionNameLookup;
}> = ({ expression, expressionColoringConditionNameLookup }) => {
  return (
    <Card subtitle={"Experiment"} title={expressionColoringConditionNameLookup[expression.condition]} noBorder>
      <CardRowDetail>
        <CardRowDetailColumn>
          <CardRowDetailLine title={"Experiment"}>
            {expressionColoringConditionNameLookup[expression.condition]}
          </CardRowDetailLine>
          <CardRowDetailLine title={"Expression"}>
            {expression.expressionValue ? expression.expressionValue : "-"}
          </CardRowDetailLine>
        </CardRowDetailColumn>
      </CardRowDetail>
    </Card>
  );
};

export interface FeatureTooltipDefaultData {
  tooltipData: IPileupFeature;
  groups?: Map<Project, Group[]>;
}

const TrackFeatureTooltip: FC<
  {
    expressionColoringConditionNameLookup: IConditionNameLookup;
  } & FeatureTooltipDefaultData
> = ({ tooltipData, expressionColoringConditionNameLookup, groups }) => {
  return (
    <Card
      title={tooltipData.feature.name}
      subtitle={capitalizeFirstLetterOfAllWords(FEATURE_DISPLAY_NAME[tooltipData.feature.type])}
      actions={[<SelectRangeButton range={tooltipData.feature.region?.range} />]}
      scrollable
      noBorder
    >
      <CardRowDetail>
        <CardRowDetailColumn>
          <CardRowDetailLine title={"Name"} key={"name"}>
            {tooltipData.feature.type === ENTITY_TYPE.MARKER_NUS ||
            tooltipData.feature.type === ENTITY_TYPE.COORDINATE ? (
              tooltipData.feature.name // temporary solution until we have marker/coordinate summary pages
            ) : (
              <SmartLink
                to={`/${FEATURE_DISPLAY_NAME[tooltipData.feature.type]}/${tooltipData.feature.targetId}`}
                forceExternal
              >
                {tooltipData.feature.name}
              </SmartLink>
            )}
          </CardRowDetailLine>
          {tooltipData.feature.description && (
            <CardRowDetailLine title={"Description"} key={"desc"} wrap>
              {tooltipData.feature.description}
            </CardRowDetailLine>
          )}
          {tooltipData.feature.synonyms?.length > 0 && (
            <CardRowDetailLine title={"Synonyms"} key={"syn"} wrap>
              {tooltipData.feature.synonyms.slice(0, 5).join(", ")}
              {tooltipData.feature.synonyms?.length > 5 && ",..."}
            </CardRowDetailLine>
          )}
          {tooltipData.feature.sequenceTypeId && (
            <CardRowDetailLine title={"Sequence Type"} key={"seqType"} wrap>
              {SEQUENCE_TYPE_LABEL[tooltipData.feature.sequenceTypeId]}
            </CardRowDetailLine>
          )}
          {tooltipData.feature.status && (
            <CardRowDetailLine title={"Status"} key={"status"} wrap>
              {tooltipData.feature.status}
            </CardRowDetailLine>
          )}
          <CardRowDetailLineSpacer />
          <CardRowDetailLine title={"Region"} key={"region"}>
            {formatNumberByThousands(tooltipData.feature.region?.range.start)} &ndash;{" "}
            {formatNumberByThousands(tooltipData.feature.region?.range.stop)}
          </CardRowDetailLine>
          <CardRowDetailLine title={"Length"} key={"length"}>
            {formatNumberByThousands(
              tooltipData.feature.region?.range.stop - tooltipData.feature.region?.range.start + 1,
            )}{" "}
            bp
          </CardRowDetailLine>
          {tooltipData.feature.type !== ENTITY_TYPE.MARKER_NUS && (
            <CardRowDetailLine title={"Strand"} key={"strand"}>
              {tooltipData.feature.region?.strand}
            </CardRowDetailLine>
          )}
          {tooltipData.feature.extReferences?.length > 0 && (
            <CardRowDetailLine title={"External References"} key={"extRef"} wrap>
              {tooltipData.feature.extReferences.map((e, i) => (
                <div key={`feature-tooltips-row${i}`}>
                  <a target="_blank" rel="noopener noreferrer" title={`Open link in new tab`} href={e.link}>
                    {e.displayName}
                  </a>
                </div>
              ))}
            </CardRowDetailLine>
          )}
        </CardRowDetailColumn>
      </CardRowDetail>
      {!!tooltipData.expressionParams?.length && (
        <CardRowDetail title={"Expressions"}>
          <CardRowDetailColumn>
            {tooltipData.expressionParams.map((expression, index) => {
              return (
                <CardRowDetailLine
                  title={expressionColoringConditionNameLookup[expression.condition]}
                  key={`expression-${index}`}
                >
                  {expression.expressionValue ? expression.expressionValue : "-"}
                </CardRowDetailLine>
              );
            })}
          </CardRowDetailColumn>
        </CardRowDetail>
      )}
      {[...groups.entries()].map(([project, groups], index) => (
        <ProjectGroupsRow project={project} groups={groups} key={`feature-tooltip-project-groups-row-${index}`} />
      ))}
    </Card>
  );
};

const TrackAaFeatureTooltip: FC<FeatureTooltipDefaultData> = ({ tooltipData, groups }) => {
  return (
    <Card
      title={tooltipData.feature.name}
      subtitle={capitalizeFirstLetterOfAllWords(FEATURE_DISPLAY_NAME[tooltipData.feature.type])}
      actions={[<SelectRangeButton range={tooltipData.feature.region?.range} />]}
      noBorder
    >
      <CardRowDetail>
        <CardRowDetailColumn>
          <CardRowDetailLine title={"Name"}>
            <SmartLink to={`/${ENTITY_TYPE.AAFEATURE}/${tooltipData.feature.id}`} forceExternal>
              {tooltipData.feature.name}
            </SmartLink>
          </CardRowDetailLine>
          {tooltipData.feature.description && (
            <CardRowDetailLine title={"Description"} wrap>
              {tooltipData.feature.description}
            </CardRowDetailLine>
          )}
          <CardRowDetailLineSpacer />
          <CardRowDetailLine title={"Protein"}>
            <SmartLink to={`/${ENTITY_TYPE.PROTEIN}/${tooltipData.feature.protein.id}`} forceExternal>
              {tooltipData.feature.protein.sequenceId}
            </SmartLink>
          </CardRowDetailLine>
          <CardRowDetailLine title={"Location (Protein)"}>
            {formatNumberByThousands(tooltipData.feature.origRange?.start)} &ndash;{" "}
            {formatNumberByThousands(tooltipData.feature.origRange?.stop)}
          </CardRowDetailLine>
          <CardRowDetailLine title={"Location (Contig)"}>
            {formatNumberByThousands(tooltipData.feature.region?.range.start)} &ndash;{" "}
            {formatNumberByThousands(tooltipData.feature.region?.range.stop)}
          </CardRowDetailLine>
          <CardRowDetailLine title={"Strand"}>{tooltipData.feature.region?.strand}</CardRowDetailLine>
          <CardRowDetailLine title={"Score"}>{tooltipData.feature.score}</CardRowDetailLine>
          <CardRowDetailLine title={"E/P-Value"}>{tooltipData.feature.eValue}</CardRowDetailLine>
          <CardRowDetailLine title={"Database"}>{tooltipData.feature.extDatabaseName}</CardRowDetailLine>
        </CardRowDetailColumn>
      </CardRowDetail>
      {[...groups.entries()].map(([project, groups], index) => (
        <ProjectGroupsRow project={project} groups={groups} key={`feature-tooltip-project-groups-row-${index}`} />
      ))}
    </Card>
  );
};

const TrackNuFeatureTooltip: FC<FeatureTooltipDefaultData> = ({ tooltipData, groups }) => {
  return (
    <Card
      title={tooltipData.feature.name}
      subtitle={capitalizeFirstLetterOfAllWords(FEATURE_DISPLAY_NAME[tooltipData.feature.type])}
      actions={[<SelectRangeButton range={tooltipData.feature.region?.range} />]}
      noBorder
    >
      <CardRowDetail>
        <CardRowDetailColumn>
          <CardRowDetailLine title={"Name"}>
            <SmartLink to={`/${ENTITY_TYPE.NUFEATURE}/${tooltipData.feature.id}`} forceExternal>
              {tooltipData.feature.name}
            </SmartLink>
          </CardRowDetailLine>
          {tooltipData.feature.description && (
            <CardRowDetailLine title={"Description"} wrap>
              {tooltipData.feature.description}
            </CardRowDetailLine>
          )}
          <CardRowDetailLine title={"Location (Contig)"}>
            {formatNumberByThousands(tooltipData.feature.region?.range.start)} &ndash;{" "}
            {formatNumberByThousands(tooltipData.feature.region?.range.stop)}
          </CardRowDetailLine>
          <CardRowDetailLine title={"Strand"}>{tooltipData.feature.region?.strand}</CardRowDetailLine>
          {tooltipData.feature.extReferences?.length > 0 && (
            <CardRowDetailLine title={"External References"} wrap>
              {tooltipData.feature.extReferences.map((e, i) => (
                <div key={`feature-tooltips-row${i}`}>
                  <a target="_blank" rel="noopener noreferrer" title={`Open link in new tab`} href={e.link}>
                    {e.displayName}
                  </a>
                </div>
              ))}
            </CardRowDetailLine>
          )}
          <CardRowDetailLineSpacer />
        </CardRowDetailColumn>
      </CardRowDetail>
      {[...groups.entries()].map(([project, groups], index) => (
        <ProjectGroupsRow project={project} groups={groups} key={`feature-tooltip-project-groups-row-${index}`} />
      ))}
    </Card>
  );
};

const TrackBedTooltip: FC<FeatureTooltipDefaultData> = ({ tooltipData }) => {
  const bedItem: Feature = tooltipData.feature;
  return (
    <Card
      title={tooltipData.feature.name}
      subtitle={capitalizeFirstLetterOfAllWords(FEATURE_DISPLAY_NAME[bedItem.type])}
      actions={[<SelectRangeButton range={tooltipData.feature.region?.range} />]}
      noBorder
    >
      <CardRowDetail>
        <CardRowDetailColumn>
          <CardRowDetailLine title={"Name"}>{bedItem.name}</CardRowDetailLine>
          {bedItem.description && (
            <CardRowDetailLine title={"Description"} wrap>
              {bedItem.description}
            </CardRowDetailLine>
          )}
          <CardRowDetailLineSpacer />
          <CardRowDetailLine title={"Region"}>
            {formatNumberByThousands(bedItem.region?.range.start)} &ndash;{" "}
            {formatNumberByThousands(bedItem.region?.range.stop)}
          </CardRowDetailLine>
          <CardRowDetailLine title={"Length"}>
            {formatNumberByThousands(bedItem.region?.range.stop - bedItem.region?.range.start + 1)} bp
          </CardRowDetailLine>
          <CardRowDetailLine title={"Strand"}>{bedItem.region?.strand}</CardRowDetailLine>
          <CardRowDetailLine title={"Score"}>{bedItem.score}</CardRowDetailLine>
        </CardRowDetailColumn>
      </CardRowDetail>
    </Card>
  );
};

export { TrackAaFeatureTooltip, TrackBedTooltip, TrackExpressionTooltip, TrackFeatureTooltip, TrackNuFeatureTooltip };
